import { snakeCase } from "./strings";

interface FormProps {
  fields?:
    | {
        name: string | number | (string | number)[];
        value: unknown;
        errors?: string[];
        touched?: boolean;
      }[]
    | undefined;
}

type FieldData = Exclude<FormProps["fields"], undefined>[number];

interface FormError {
  field: string;
  messages: string[];
}

// The API puts the failing entry's position inside the message, 1-based:
// "2 :: Enter a valid email address."
const LIST_POSITION = /^(\d+)\s*::\s*/;

export function stripListPosition(message: string): string {
  return message.replace(LIST_POSITION, "");
}

/**
 * Moves a list's errors from the list itself onto the entries that failed, so
 * they show next to the right input instead of under the add button.
 *
 * Positions count the entries that were sent, and the submit drops the empty
 * ones (InputList always seeds one), so they are mapped back through the same
 * filter. Messages with no position stay on the list.
 */
export function getFormListErrors(
  errors: FormError[],
  values: Record<string, unknown>,
): FieldData[] {
  const fieldData: FieldData[] = [];

  for (const error of errors) {
    const list = values[error.field];
    if (!Array.isArray(list)) continue;

    const sent = list.map((item, index) => ({ item, index })).filter(({ item }) => !!item);
    const messagesByIndex = new Map<number, string[]>();
    const listMessages: string[] = [];

    for (const message of error.messages) {
      const position = message.match(LIST_POSITION);
      const entry = position && sent[Number(position[1]) - 1];
      if (!entry) {
        listMessages.push(message);
        continue;
      }
      const previous = messagesByIndex.get(entry.index) ?? [];
      messagesByIndex.set(entry.index, [...previous, stripListPosition(message)]);
    }

    if (messagesByIndex.size === 0) continue;

    fieldData.push({ name: [error.field], value: list, errors: listMessages });
    for (const [index, messages] of messagesByIndex) {
      fieldData.push({ name: [error.field, index], value: list[index], errors: messages });
    }
  }

  return fieldData;
}

export function getFormErrors(
  errors: FormError[],
  values: Record<string, unknown>,
): FieldType[] | undefined {
  if (errors && errors.length > 0) {
    const fieldData: FieldType[] = [];
    for (const key in values) {
      let keyError = key;

      if (keyError === "defaultCounterparts") {
        keyError = "defaultCounterpart";
      }

      const error = errors.find(
        item => item.field === snakeCase(keyError) || item.field === keyError,
      );
      fieldData.push({
        name: key,
        value: values[key],
        errors: error?.messages || [],
      });
    }

    return fieldData;
  }
}

export function getFormSuccess(values: Record<string, unknown>): FieldType[] | undefined {
  const fieldData: FieldType[] = [];
  for (const key in values) {
    fieldData.push({
      name: key,
      value: values[key],
      errors: [],
    });
  }
  return fieldData;
}

interface GetFormListErrorsInputProps {
  data: FormError[];
  values: Record<string, unknown>;
  formList: string;
  formListIndex: number;
}

export function getFormListErrorsInput({
  data,
  values,
  formList,
  formListIndex,
}: GetFormListErrorsInputProps): FieldData[] | undefined {
  if (data.length > 0) {
    let fieldData: FieldData[] = [];
    for (const prop in values) {
      const error = data.find(item => {
        return item.field === prop && item.messages.length > 0;
      });
      fieldData = [
        ...fieldData,
        {
          name: [formList, formListIndex, prop],
          value: values[prop],
          touched: true,
          errors: error ? error.messages : [],
        },
      ];
    }

    return fieldData;
  }
}
